import React, { createContext, useContext } from 'react';
import { useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { useSafeAreaFrame, useSafeAreaInsets } from 'react-native-safe-area-context';

import { useFloorStyle } from '../utils/floorStyles';
import { useBottomOffset } from '../utils/useBottomOffset';

export const FloorContext = createContext();

export const FloorProvider = ({ children, value }) => {
  const { height } = useSafeAreaFrame();
  const insets = useSafeAreaInsets();
  const translateFront = -0.019 * 1.633 * 0.82 * (height - insets.top - insets.bottom);
  const translateSide = 0.1525 * 1.633 * 0.82 * (height - insets.top - insets.bottom);
  const translateRoofTop = -0.028 * 1.633 * 0.82 * (height - insets.top - insets.bottom);

  const translateFirstFloorFront = useSharedValue(translateFront);
  const translateFirstFloorSide = useSharedValue(translateSide);
  const translateSecondFloorFront = useSharedValue(translateFront);
  const translateSecondFloorSide = useSharedValue(translateSide);
  const translateThirdFloorFront = useSharedValue(translateFront);
  const translateThirdFloorSide = useSharedValue(translateSide);
  const translateRoofTopFront = useSharedValue(translateRoofTop);

  const firstFlootBottomOffset = useBottomOffset(0.062, 0.1638);
  const secondFloorBottomOffset = useBottomOffset(0.062, 0.332935);
  const thirdFloorBottomOffset = useBottomOffset(0.062, 0.50207);

  const firstFloorFrontStyle = useFloorStyle(
    2.004184,
    translateFirstFloorFront,
    firstFlootBottomOffset
  );

  const firstFloorSideStyle = useFloorStyle(0.64, translateFirstFloorSide, firstFlootBottomOffset);
  const secondFloorFrontStyle = useFloorStyle(
    2.004184,
    translateSecondFloorFront,
    secondFloorBottomOffset
  );
  const secondFloorSideStyle = useFloorStyle(
    0.64,
    translateSecondFloorSide,
    secondFloorBottomOffset
  );

  const thirdFloorFrontStyle = useFloorStyle(
    2.004184,
    translateThirdFloorFront,
    thirdFloorBottomOffset
  );
  const thirdFloorSideStyle = useFloorStyle(0.64, translateThirdFloorSide, thirdFloorBottomOffset);

  const roofTopFrontStyle = useAnimatedStyle(() => {
    return {
      height: 0.0576 * (height - insets.top - insets.bottom),
      width: 3.75473684 * 0.0576 * (height - insets.top - insets.bottom),
      alignSelf: 'center',
      transform: [{ translateX: translateRoofTopFront.value }],
      position: 'absolute',
      bottom:
        0.062 * height +
        0.176 * (height - insets.top - insets.bottom) +
        3 * 0.169135 * (height - insets.top - insets.bottom),
    };
  }, [insets, height]);

  return (
    <FloorContext.Provider
      value={{
        translateFront,
        translateSide,
        translateRoofTop,
        translateFirstFloorFront,
        translateFirstFloorSide,
        translateSecondFloorFront,
        translateSecondFloorSide,
        translateThirdFloorFront,
        translateThirdFloorSide,
        translateRoofTopFront,
        firstFloorFrontStyle,
        firstFloorSideStyle,
        secondFloorSideStyle,
        secondFloorFrontStyle,
        thirdFloorFrontStyle,
        thirdFloorSideStyle,
        roofTopFrontStyle,
      }}>
      {children}
    </FloorContext.Provider>
  );
};

export const useFloor = () => {
  const context = useContext(FloorContext);
  if (!context) throw new Error('useFloor must be used within a FloorProvider');
  return context;
};
